/**
 * @module InputFieldFormatters
 * @description Provides formatting and parsing utilities for form input field values.
 * This module contains a collection of INPUT_FIELD_FORMATTERS that handle different data types
 * and their display representations in the form.
 */

export interface InputFieldFormatter {
  parse(value: string | number): string;
  format(value: string | number): string;
  getNewCursorPosition(initialInputValue: string, intialCursorPosition: number | null): number;
}

abstract class NumberFieldFormatter implements InputFieldFormatter {

  protected abstract SUPPORTED_DECIMAL_PLACES: number;
  protected abstract customFormat(number: number): string;

  parse(value: string | number): string {
    return value.toString().replace(/[^0-9.]/g, '');
  }

  format(value: string | number): string {
    let parsedValue = this.parse(value);

    const startsWithZeroCount = parsedValue.match(/^0+/)?.[0].length || 0;
    const endsWithDot = this.SUPPORTED_DECIMAL_PLACES > 0 && parsedValue.endsWith('.');
    const endsWithZeroAfterDot = parsedValue.match(/\.0+$/)?.[0] || '';
    const endsWithZeroAfterDotCount = this.SUPPORTED_DECIMAL_PLACES > 0 ? (endsWithZeroAfterDot ? endsWithZeroAfterDot.length - 1 : 0) : 0;

    if (startsWithZeroCount > 0) {
      // Temporarily change the first zero to 1 for formatting
      parsedValue = '1' + parsedValue.slice(1);
    }

    if (endsWithZeroAfterDotCount > 0) {
      // Temporarily change the last zero to 1 for formatting
      parsedValue = parsedValue.slice(0, -1) + '1';
    }

    const number = this.getNumber(parsedValue);
    const retVal = (number === null) ? '' : this.customFormat(number);

    // Break retVal into prefix (e.g. $), number part (123,000.05), and suffix (e.g. %)
    const match = retVal.match(/^([^0-9,.]*)([0-9,.]*)(.*)$/);
    if (!match) return retVal;
    const [_, prefix, numberPart, suffix] = match;
    let newNumberPart = numberPart;

    if (startsWithZeroCount > 0) {
      // Restore the original leading zero
      newNumberPart = '0' + newNumberPart.slice(1);
    }
    if (endsWithZeroAfterDotCount > 0) {
      // Restore the original trailing zero
      newNumberPart = newNumberPart.slice(0, -1) + '0';
    }
    if (endsWithDot) {
      newNumberPart += '.';
    }
    return prefix + newNumberPart + suffix;
  }

  getNewCursorPosition(initialInputValue: string, intialCursorPosition: number | null): number {
    if (intialCursorPosition === null) {
      return 0;
    }
    const formattedValue = this.format(initialInputValue);
    if (formattedValue.length == 0) {
      return 0;
    }

    const digitsBeforeCursorInInput = this.parse(initialInputValue.slice(0, intialCursorPosition)).length;
    let digitsBeforeCursorInFormatted = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (this.SUPPORTED_DECIMAL_PLACES > 0 && !/[0-9.]/.test(formattedValue[i])) continue;
      if (this.SUPPORTED_DECIMAL_PLACES === 0 && !/[0-9]/.test(formattedValue[i])) continue;
      digitsBeforeCursorInFormatted++;
      if (digitsBeforeCursorInFormatted == digitsBeforeCursorInInput) {
        return i + 1;
      } else if (digitsBeforeCursorInFormatted > digitsBeforeCursorInInput && digitsBeforeCursorInInput === 0) {
        return i;
      }
    }
    return formattedValue.length;
  }

  protected getNumber(value: string | number): number | null {
    const stringValue = this.parse(value);
    const number = parseFloat(stringValue);
    return isNaN(number) ? null : number;
  }
}

export class CurrencyFormatter extends NumberFieldFormatter {
  protected readonly SUPPORTED_DECIMAL_PLACES = 0;
  private readonly CURRENCY_FORMAT = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: this.SUPPORTED_DECIMAL_PLACES
  });

  protected customFormat(number: number): string {
    return (number === null) ? '' : this.CURRENCY_FORMAT.format(number);
  }
}

export class PercentageFormatter extends NumberFieldFormatter {
  protected readonly SUPPORTED_DECIMAL_PLACES = 2;
  protected customFormat(number: number): string {
    const retVal = (number === null) ? '' : 
      `${Number(parseFloat((Math.floor(number*100)/100).toFixed(this.SUPPORTED_DECIMAL_PLACES))).toString()}`;
    return retVal ? retVal + '%' : '';
  }
}

export class PhoneFormatter implements InputFieldFormatter {
  parse(value: string ): string {
    const phoneNumber = value.replace(/\D/g, '');
    return phoneNumber;
  }
  
  format(value: string ): string {
    const phoneNumber = value.replace(/\D/g, '');
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }

  getNewCursorPosition(_initialInputValue: string, _intialCursorPosition: number | null): number {
    return -1;
  }
}

// Create instances of each formatter
class IntegerFormatter extends NumberFieldFormatter {
  protected readonly SUPPORTED_DECIMAL_PLACES = 0;

  protected customFormat(number: number): string {
    // Format as integer with thousands separator, no decimals
    if (isNaN(number)) return '';
    return Math.trunc(number).toLocaleString('en-US');
  }
}

const percentageFormatter = new PercentageFormatter();

export const INPUT_FIELD_FORMATTERS: Record<string, InputFieldFormatter> = {
  currency: new CurrencyFormatter(),
  percentage: percentageFormatter,
  percent: percentageFormatter,
  integer: new IntegerFormatter(),
  phone: new PhoneFormatter()
};

export type FieldType = 'dropdown' | 'currency' | 'percentage' | 'percent' | 'integer' | 'phone';
